import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AOS from 'aos'
import BlockingChart from './chart'
import { useBlockingFactorApi } from '../../../../store/api/hooks/blockingFactorApiHook'
import { useParams } from 'react-router-dom'
import { ErrorResponse } from '../../../../store/api/apiTypes'
import Text from '../../../../elements/Typo/Text'
import Loader from '../../../../components/loader'
import { trackEvent } from '../../../../_common/functions'

interface Props {
    selectedGroup: string | null
    selectedKPI: string | null
    selectedDateRange: any[]
    selectedConfig?: string
    toDownloadAsReport?: boolean
}
const BlockingFactor: React.FC<Props> = ({ selectedGroup, selectedKPI, selectedDateRange, selectedConfig, toDownloadAsReport = false }) => {
    const { t } = useTranslation()
    const params = useParams()
    const [apiData, setApiData] = useState<any>(null)
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const [chartData, setChartData] = useState<any>([])
    const { getBlockingFactors } = useBlockingFactorApi()

    const [subTitle, setSubTitle] = useState<string>(t('(Test VS Control)'))

    useEffect(() => {
        if (selectedGroup) {
            setIsLoading(true)
            const postData: any = {
                literal: params.exptLiteral,
                group_name: selectedGroup,
                methodology: selectedConfig,
                kpiname: selectedKPI,
            }

            if (selectedDateRange.length == 2) {
                postData.measurement_start_date = selectedDateRange[0]
                postData.measurement_end_date = selectedDateRange[1]
                postData.is_slice_and_dice = true
            } else {
                postData.measurement_start_date = null
                postData.measurement_end_date = null
                postData.is_slice_and_dice = false
            }
            getBlockingFactors(
                postData,
                (data: any) => {
                    const values = data?.data

                    if (values) {
                        setApiData(values)
                    }
                    setIsLoading(false)
                },
                (error: ErrorResponse<[]>) => {
                    setIsLoading(false)
                },
            )
        }
    }, [selectedGroup, selectedConfig, selectedKPI, selectedDateRange])

    useEffect(() => {
        if (selectedKPI) {
            if (selectedKPI == 'Total Distribution') {
                setSubTitle(t('(Test VS Counterfactual)'))
            } else {
                setSubTitle(t('(Test VS Control)'))
            }
            const filteredData = apiData?.[selectedKPI]

            if (filteredData) {
                const data = Object.entries(filteredData).map((item, index) => {
                    return { title: t(`Average ${selectedKPI} per ${item[0]}`), subTitle, value: item[1] || 0 }
                })
                const alteredData = data?.map((item: any) => {
                    return {
                        ...item,
                        value: item.value?.map((row: any) => {
                            return {
                                Blocking_Factor_Value: row.Blocking_Factor_Value || 0,
                                [selectedKPI == 'Total Distribution' ? 'Counterfactual_Avg' : 'Control_Avg']: row.Control_Avg || 0,
                                //Control_Avg: row.Control_Avg || 0,
                                P_Value: row.P_Value || 0,
                                Test_Avg: row.Test_Avg || 0,
                                Uplift_Percentage: row.Uplift_Percentage || 'NA',
                            }
                        }),
                    }
                })

                setChartData(alteredData)
            }
        }
    }, [selectedKPI, apiData])

    useEffect(() => {
        const params: any = {}

        if (toDownloadAsReport) {
            params['disable'] = true // Animation disabled
        } else {
            params['once'] = false // Animation occurs every time you scroll to the element
        }

        AOS.init(params)
        AOS.refresh()
        trackEvent('experiment_details_blocking_factor_tab_view')
    }, [])

    if (isLoading) {
        return (
            <div className="py-10">
                <Loader />
            </div>
        )
    }

    return (
        <div className=" py-2 grid grid-cols-3 gap-3" data-aos="zoom-in" data-aos-delay="100">
            {!selectedKPI && !isLoading ? (
                <div className="col-span-3 gap-6 flex flex-col text-center py-32 bg-base-100 rounded-[0.45rem] shadow-1">
                    <Text>{t('Since no Blocking factors were selected, no graph is available.')}</Text>
                </div>
            ) : null}

            <div className="col-span-3 gap-6 flex flex-col">
                {chartData?.map((item: any, index: number) => {
                    return (
                        <div key={`${index}-chart-data-${item.value}`} className="card rounded-lg w-full p-2 shadow-2xl bg-white blocking-factor-graph">
                            <BlockingChart
                                selectedConfig={selectedConfig}
                                title={item.title}
                                subTitle={selectedConfig === 'synthetic_control' ? t('Test vs Synthetic Control') : subTitle}
                                data={item.value}
                                yaxisTitle1={selectedKPI || ''}
                                yaxisTitle2={t('uplift %')}
                                toDownloadAsReport={toDownloadAsReport}
                            />
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
export default BlockingFactor
